import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { collection, query, orderBy } from "firebase/firestore";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Avatar from "@mui/material/Avatar";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHubspot } from "@fortawesome/free-brands-svg-icons";
import { db } from "../../lib/firebase";
import { useAuth } from "../../context/AuthContext";
import { useSnackbar } from "../../context/SnackbarContext";
import { useUnsavedChangesGuard } from "../../hooks/useUnsavedChangesGuard";
import { FormPageLayout } from "../../components/forms/FormPageLayout";
import { FormSection } from "../../components/forms/FormSection";
import { HubSpotContactTypeahead } from "../../components/HubSpotContactTypeahead";
import { useFirestoreCollectionOnce } from "../../hooks/useFirestoreCollectionOnce";
import { markCodeRequestsFulfilledByEmail } from "../../services/codeRequestService";
import { useAllPersons } from "../../hooks/useAllPersons";
import {
  useCreateContact,
  useInviteToEvent,
} from "../../hooks/useUserManagement";
import {
  appendHubSpotNotesBlock,
  buildHubSpotNotesBlock,
  isHubSpotConfigured,
  HUBSPOT_ORANGE,
} from "../../utils/hubspot";
import {
  getLibraryHeadshotForContact,
  importSharePointHeadshots,
} from "../../services/headshotService";
import { ProfileTab } from "./tabs/ProfileTab";
import { ContactLogisticsTab } from "./tabs/ContactLogisticsTab";
import { InternalNotesTab } from "./tabs/InternalNotesTab";
import {
  canEditInternalNotes,
  canViewInternalNotes,
} from "./userDetailPermissions";
import {
  userEditSchema,
  type UserEditFormData,
} from "../../schemas/userProfileSchema";
import { useIsPhone } from "../../hooks/useIsPhone";
import type {
  Event,
  HubSpotContactSuggestion,
} from "@event-planner/shared";

const EMPTY_FORM: UserEditFormData = {
  honorificPrefix: null,
  honorificSuffix: null,
  firstName: "",
  lastName: "",
  role: "invitee",
  title: null,
  organization: null,
  bio: null,
  phone: null,
  linkedIn: null,
  website: null,
  workCity: null,
  workState: null,
  dietaryRestrictions: [],
  accessibilityNeeds: null,
  notes: null,
};

/** The HubSpot link picked via the typeahead, carried through submit. */
interface HubSpotStash {
  contactId: string;
  repcrd: string | null;
}

/**
 * Which tab each form field lives on (0 Profile, 1 Contact & Logistics,
 * 2 Internal Notes) — zod validates the whole schema regardless of the
 * active tab, so an invalid submit jumps to the first tab holding an
 * error instead of failing invisibly behind an inactive tab.
 */
const FIELD_TAB: Partial<Record<keyof UserEditFormData, number>> = {
  honorificPrefix: 0,
  firstName: 0,
  lastName: 0,
  honorificSuffix: 0,
  title: 0,
  organization: 0,
  bio: 0,
  role: 0,
  phone: 1,
  workCity: 1,
  workState: 1,
  linkedIn: 1,
  website: 1,
  dietaryRestrictions: 1,
  accessibilityNeeds: 1,
  notes: 2,
};

/**
 * Create a new user (pre-auth person identity), with an optional
 * "also add to an event" branch (ADR-073, IA cleaned up per ADR-092).
 *
 * Mounted at `/users/new`. RHF + zod with FULL edit-form parity
 * (ADR-169 Stage 2 folded in the ADR-125 conversion): the same
 * `userEditSchema` and tab section components as `UserDetailPage`,
 * rendered stacked, plus create-only additions — required email (with
 * HubSpot contact typeahead when configured) and the optional event
 * picker. One mechanical exception: photo upload stays post-create
 * (the storage path needs an existing personId).
 *
 * Picking a HubSpot suggestion stashes `{contactId, repcrd}` and
 * prefills empty fields client-side — including the "From HubSpot"
 * notes block — for review before Save (ADR-172 review-first model).
 * Submit passes the stash through `createContact`, so the person is
 * born linked; there is no server-side pull. Manually editing the
 * email after a pick clears the stash.
 *
 * The page submit calls `createContact` and (when an event is picked)
 * `inviteToEvent`. No email is sent on submit per ADR-092; invitation
 * emails are sent later from the Attendees page Send Emails button.
 *
 * Pre-fill via search params:
 *   ?prefillEmail=<email>&prefillEventId=<eventId>
 */
export function UserCreatePage() {
  const isPhone = useIsPhone();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showSuccess, showError, showWarning } = useSnackbar();
  const { user, role: callerRole } = useAuth();

  const { data: allPersons } = useAllPersons();

  // Events list for the optional "add to event" branch.
  const eventsQuery = useMemo(
    () => query(collection(db, "events"), orderBy("startDate", "asc")),
    [],
  );
  const { data: events, loading: loadingEvents } =
    useFirestoreCollectionOnce<Event>(eventsQuery);

  const { create: createContactFn, loading: creating } = useCreateContact();
  const { invite, loading: inviting } = useInviteToEvent();

  const form = useForm<UserEditFormData>({
    resolver: zodResolver(userEditSchema),
    defaultValues: EMPTY_FORM,
  });

  // Create-only state, outside RHF: the email join key, the optional
  // event, and the stashed HubSpot link.
  const [email, setEmail] = useState(searchParams.get("prefillEmail") ?? "");
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [hubspotStash, setHubspotStash] = useState<HubSpotStash | null>(null);
  /**
   * Library photo staged for the picked contact (ADR-181 link-time pull).
   * Tagged with its contactId — it only renders/commits while the stash
   * still points at that contact, so clearing the pick (manual email
   * edit, banner close) orphans it harmlessly.
   */
  const [pendingHeadshot, setPendingHeadshot] = useState<{
    contactId: string;
    driveItemId: string;
    fileName: string;
    thumbnailUrl: string | null;
  } | null>(null);
  /** ContactId whose library lookup is still in flight — drives the spinner. */
  const [headshotLookupFor, setHeadshotLookupFor] = useState<string | null>(
    null,
  );
  const stagedHeadshot =
    pendingHeadshot && hubspotStash?.contactId === pendingHeadshot.contactId
      ? pendingHeadshot
      : null;
  const headshotLookupPending =
    !!hubspotStash && headshotLookupFor === hubspotStash.contactId;
  const [error, setError] = useState<string | null>(null);
  const [submittingFlag, setSubmittingFlag] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);

  // Resolve prefillEventId once events finish loading.
  const prefillEventId = searchParams.get("prefillEventId");
  useEffect(() => {
    if (!prefillEventId || !events.length) return;
    const match = events.find((e) => e.id === prefillEventId);
    if (match) setSelectedEvent(match);
  }, [prefillEventId, events]);

  // Duplicate detection (ADR-136): a `persons` row covers both claimed
  // and pre-claim people, so dedup is a single email lookup.
  const duplicateMatch = useMemo(() => {
    const normalized = email.trim().toLowerCase();
    if (!normalized) return null;
    const personWithEmail = allPersons.find(
      (p) => p.email.toLowerCase() === normalized,
    );
    if (personWithEmail) {
      return {
        kind: personWithEmail.authUid === null
          ? ("contact" as const)
          : ("user" as const),
        displayName: personWithEmail.displayName || personWithEmail.email,
      };
    }
    return null;
  }, [email, allPersons]);

  const submitting =
    creating || inviting || submittingFlag || form.formState.isSubmitting;
  const isDirty =
    form.formState.isDirty ||
    !!email.trim() ||
    !!selectedEvent ||
    !!hubspotStash;

  const { dialog: unsavedGuardDialog } = useUnsavedChangesGuard(
    isDirty,
    submitting,
  );

  const showInternalNotes = canViewInternalNotes(callerRole);
  // Same index formula as UserDetailPage — Events is the last tab.
  const eventsTabIndex = showInternalNotes ? 3 : 2;

  /** Prefill a form field from a suggestion, but never clobber typed input. */
  function fillIfEmpty(
    name: keyof UserEditFormData,
    value: string | null | undefined,
  ) {
    if (!value) return;
    const current = form.getValues(name);
    if (current == null || current === "") {
      form.setValue(name, value, { shouldDirty: true });
    }
  }

  function handleHubSpotPick(suggestion: HubSpotContactSuggestion) {
    if (suggestion.email) setEmail(suggestion.email);
    setHubspotStash({
      contactId: suggestion.contactId,
      repcrd: suggestion.repcrd ?? null,
    });
    // Library lookup (ADR-181): a photo, if found, joins the review as a
    // staged avatar; a spinner runs while the check is in flight. Failures
    // degrade to "no photo" and never block the pick.
    setPendingHeadshot(null);
    setHeadshotLookupFor(suggestion.contactId);
    getLibraryHeadshotForContact({ contactId: suggestion.contactId })
      .then((response) => {
        if (response.headshot) {
          setPendingHeadshot({
            contactId: suggestion.contactId,
            ...response.headshot,
          });
        }
      })
      .catch(() => {})
      .finally(() => {
        setHeadshotLookupFor((prev) =>
          prev === suggestion.contactId ? null : prev,
        );
      });
    fillIfEmpty("firstName", suggestion.firstName);
    fillIfEmpty("lastName", suggestion.lastName);
    fillIfEmpty("title", suggestion.jobTitle);
    fillIfEmpty("organization", suggestion.company);
    fillIfEmpty("linkedIn", suggestion.linkedIn);
    fillIfEmpty("phone", suggestion.phone);
    fillIfEmpty("workCity", suggestion.city);
    fillIfEmpty("workState", suggestion.state);
    // Facts with no structured home land in the notes field for review
    // before Save — same review-first motion as linking (ADR-172).
    if (showInternalNotes) {
      const block = buildHubSpotNotesBlock(suggestion);
      if (block) {
        const { notes, appended } = appendHubSpotNotesBlock(
          form.getValues("notes"),
          block,
        );
        if (appended) form.setValue("notes", notes, { shouldDirty: true });
      }
    }
  }

  function handleEmailChange(next: string, reason: string) {
    setEmail(next);
    // Manual edits invalidate a picked link — the stash must always
    // describe the email in the field (guard against stale links).
    if (reason === "input" && hubspotStash) setHubspotStash(null);
  }

  /**
   * If creating + inviting fulfilled a pending code request for this email,
   * mark the request as fulfilled so the banner on the Users page clears.
   */
  async function markMatchingCodeRequestsFulfilled(createdEmail: string) {
    try {
      await markCodeRequestsFulfilledByEmail(
        createdEmail,
        user?.uid ?? "unknown",
      );
    } catch {
      // non-fatal; the banner just persists until refresh or dismissal
    }
  }

  async function onSubmit(values: UserEditFormData) {
    setError(null);

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError("Email is required.");
      return;
    }
    if (duplicateMatch) {
      setError(
        `A ${duplicateMatch.kind === "user" ? "user" : "contact"} already exists for this email.`,
      );
      return;
    }

    setSubmittingFlag(true);
    try {
      const contactResult = await createContactFn({
        email: trimmedEmail,
        role: values.role,
        firstName: values.firstName.trim() || null,
        lastName: values.lastName.trim() || null,
        honorificPrefix: values.honorificPrefix || null,
        honorificSuffix: values.honorificSuffix || null,
        title: values.title || null,
        organization: values.organization || null,
        workCity: values.workCity || null,
        workState: values.workState || null,
        bio: values.bio || null,
        phone: values.phone || null,
        linkedIn: values.linkedIn || null,
        website: values.website || null,
        dietaryRestrictions: values.dietaryRestrictions ?? [],
        accessibilityNeeds: values.accessibilityNeeds || null,
        internalNotes: values.notes || null,
        hubspotContactId: hubspotStash?.contactId ?? null,
        hubspotRepcrd: hubspotStash?.repcrd ?? null,
      });

      if (contactResult.alreadyExists) {
        setError("A contact already exists for this email.");
        setSubmittingFlag(false);
        return;
      }

      // Staged library photo commits AFTER create (ADR-181): the person
      // is born linked (hubspotContactId went through createContact), so
      // the import callable's HubSpotID re-verification passes. Failure
      // never undoes the create.
      if (stagedHeadshot) {
        try {
          const importResponse = await importSharePointHeadshots({
            items: [
              {
                driveItemId: stagedHeadshot.driveItemId,
                personId: contactResult.contactId,
              },
            ],
          });
          const outcome = importResponse.results[0];
          if (outcome?.status !== "imported") {
            showWarning(
              `Created, but the library photo wasn't imported: ${outcome?.message ?? outcome?.status ?? "unknown result"}`,
            );
          }
        } catch (err) {
          showWarning(
            `Created, but the library photo import failed: ${err instanceof Error ? err.message : "unknown error"}`,
          );
        }
      }

      const name =
        [values.firstName.trim(), values.lastName.trim()]
          .filter(Boolean)
          .join(" ") || trimmedEmail;

      // Optional: also add to an event in the same submit. No email is
      // sent here per ADR-092.
      if (selectedEvent) {
        const inviteResult = await invite({
          email: trimmedEmail,
          eventId: selectedEvent.id,
        });
        await markMatchingCodeRequestsFulfilled(trimmedEmail);
        if (inviteResult.alreadyMember) {
          showSuccess(
            `${name} added (already a member of ${selectedEvent.name}).`,
          );
        } else {
          showSuccess(`${name} added to ${selectedEvent.name}.`);
        }
        navigate("/users");
        return;
      }

      await markMatchingCodeRequestsFulfilled(trimmedEmail);
      showSuccess(`${name} added as a contact.`);
      navigate("/users");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Operation failed.";
      setError(msg);
      showError(msg);
      setSubmittingFlag(false);
    }
  }

  // FormPageLayout's <form> dispatches its native submit event into this
  // handler (typed as () => void); preventDefault, then let RHF validate.
  // On validation failure, jump to the first tab that holds an error —
  // fields on inactive tabs aren't mounted, so without this the submit
  // would fail with nothing visible.
  function handleFormSubmit(e?: React.FormEvent) {
    e?.preventDefault();
    void form.handleSubmit(onSubmit, (errors) => {
      const tabs = Object.keys(errors).map(
        (field) => FIELD_TAB[field as keyof UserEditFormData] ?? 0,
      );
      const target = Math.min(...tabs);
      setTabIndex(target === 2 && !showInternalNotes ? 0 : target);
    })();
  }

  return (
    <FormProvider {...form}>
      {unsavedGuardDialog}
      <FormPageLayout
        title="New User"
        onSubmit={handleFormSubmit}
        error={error}
        submitting={submitting}
        submitLabel={selectedEvent ? "Add & Invite" : "Add User"}
        testIdSlug="user"
      >
        <FormSection title="Identity">
          {isHubSpotConfigured() ? (
            <HubSpotContactTypeahead
              value={email}
              onChange={handleEmailChange}
              onPick={handleHubSpotPick}
              disabled={submitting}
            />
          ) : (
            <TextField
              label="Email"
              type="email"
              required
              value={email}
              onChange={(e) => handleEmailChange(e.target.value, "input")}
              fullWidth
              inputProps={{ "data-testid": "user-create-email-input" }}
            />
          )}

          {hubspotStash && (
            <Alert
              severity="info"
              icon={
                <FontAwesomeIcon
                  icon={faHubspot}
                  style={{ fontSize: 18, color: HUBSPOT_ORANGE }}
                />
              }
              onClose={() => setHubspotStash(null)}
              sx={{ mt: 1.5 }}
              data-testid="user-create-hubspot-stash-banner"
            >
              Will be linked to this HubSpot contact when you save — CRM
              details are prefilled below for review.
            </Alert>
          )}

          {headshotLookupPending && (
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ mt: 1.5 }}
              data-testid="user-create-pending-headshot-loading"
            >
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Checking the Headshot Library for a photo…
              </Typography>
            </Stack>
          )}
          {stagedHeadshot && (
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ mt: 1.5 }}
              data-testid="user-create-pending-headshot"
            >
              <Avatar
                src={stagedHeadshot.thumbnailUrl ?? undefined}
                alt="Staged library photo"
                sx={{
                  width: 40,
                  height: 40,
                  border: "2px dashed",
                  borderColor: "info.main",
                }}
              />
              <Typography variant="body2" color="text.secondary">
                Photo from the Headshot Library — imports when you save.
              </Typography>
            </Stack>
          )}

          {duplicateMatch && (
            <Alert
              severity="warning"
              sx={{ mt: 2 }}
              data-testid="user-create-duplicate-warning"
            >
              A {duplicateMatch.kind === "user" ? "user" : "contact"} already
              exists for this email:{" "}
              <strong>{duplicateMatch.displayName}</strong>. Use the search on
              the users list to find them.
            </Alert>
          )}
        </FormSection>

        {/* Same four-tab chrome as UserDetailPage. The Events tab holds
            the optional add-to-event picker (the create-time equivalent
            of the edit page's participations list). Section values live
            in RHF form state, so switching tabs never loses input. */}
        <Tabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          variant={isPhone ? "scrollable" : "standard"}
          sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}
        >
          <Tab label="Profile" />
          <Tab label="Contact & Logistics" />
          {showInternalNotes && <Tab label="Internal Notes" />}
          <Tab label="Events" />
        </Tabs>

        {tabIndex === 0 && (
          <ProfileTab createMode testIdPrefix="user-create" />
        )}
        {tabIndex === 1 && <ContactLogisticsTab />}
        {showInternalNotes && tabIndex === 2 && (
          <InternalNotesTab
            editable={canEditInternalNotes(callerRole)}
            testIdPrefix="user-create"
          />
        )}
        {tabIndex === eventsTabIndex && (
          <Paper sx={{ p: isPhone ? 2 : 3 }}>
            <FormSection title="Add to event (optional)">
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                They'll be added to the event roster. Invitation emails are
                sent separately from the Attendees page Send Emails button.
              </Typography>
              <Autocomplete
                options={events}
                loading={loadingEvents}
                value={selectedEvent}
                onChange={(_, value) => setSelectedEvent(value)}
                getOptionLabel={(event) => event.name}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Event"
                    placeholder="Leave blank to add as a contact only"
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingEvents ? (
                              <CircularProgress size={18} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      },
                      htmlInput: {
                        ...params.inputProps,
                        "data-testid": "user-create-event-input",
                      },
                    }}
                  />
                )}
              />
            </FormSection>
          </Paper>
        )}
      </FormPageLayout>
    </FormProvider>
  );
}
